/**
 * Configuration déclarative pour l'entité School
 * Source unique de vérité pour tous les champs de School
 */

import type { School } from '../../types/school';
import { defineEntityConfig } from '../../lib/entity-config';
import { SCHOOL_TYPES, POSITIONINGS, COUNTRIES } from '../../types/school';

export const SCHOOL_CONFIG = defineEntityConfig<School>({
  entityName: 'école',
  entityNamePlural: 'écoles',

  fields: {
    name: {
      key: 'name',
      label: 'École',
      type: 'text',
      required: true,
      sortable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 250,
      importValidation: (value) => {
        if (typeof value !== 'string') return 'Le nom doit être une chaîne de caractères';
        if (value.length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return true;
      },
    },

    postal_code: {
      key: 'postal_code',
      label: 'Code postal',
      type: 'text',
      sortable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 120,
    },

    city: {
      key: 'city',
      label: 'Ville',
      type: 'text',
      sortable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    country: {
      key: 'country',
      label: 'Pays',
      type: 'enum',
      enumValues: COUNTRIES.map((c) => c.code) as any,
      required: false,
      sortable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 120,
      exportTransform: (value: string) => {
        const country = COUNTRIES.find((c) => c.code === value);
        return country?.label || value;
      },
      importTransform: (value: string) => {
        // Accepte code ou label
        const byCode = COUNTRIES.find((c) => c.code === value);
        if (byCode) return byCode.code;
        const byLabel = COUNTRIES.find((c) => c.label.toLowerCase() === value.toLowerCase());
        return byLabel?.code || 'FRA'; // Défaut: France
      },
    },

    type: {
      key: 'type',
      label: 'Type',
      type: 'enum',
      enumValues: SCHOOL_TYPES as any,
      sortable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 180,
    },

    positioning: {
      key: 'positioning',
      label: 'Positionnement',
      type: 'enum',
      enumValues: POSITIONINGS as any,
      sortable: true,
      editable: true,
      showInTable: false, // Pas visible par défaut
      showInFilters: true,
      exportable: true, // Exportable si colonne activée
      importable: true,
      size: 150,
    },

    contacts_count: {
      key: 'contacts',
      label: 'Contacts',
      type: 'number',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true, // Doit être exporté via le transformer custom
      importable: false,
    },

    // Personne à contacter
    contact_first_name: {
      key: 'contact_first_name',
      label: 'Prénom contact',
      type: 'text',
      sortable: true,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    contact_last_name: {
      key: 'contact_last_name',
      label: 'Nom contact',
      type: 'text',
      sortable: true,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    contact_email: {
      key: 'contact_email',
      label: 'Email contact',
      type: 'text',
      sortable: true,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 200,
      importValidation: (value) => {
        if (!value) return true; // Optionnel
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(value))) return 'Email invalide';
        return true;
      },
    },

    contact_phone: {
      key: 'contact_phone',
      label: 'Téléphone contact',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
    },

    address: {
      key: 'address',
      label: 'Adresse',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true, // Exportable si colonne activée
      importable: true,
      exportTransform: (value: string | null) => value || '',
    },

    notes: {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: true,
      exportable: true, // Exportable si colonne activée
      importable: true,
      exportTransform: (value: string | null) => value || '',
    },
  },
});
